import { getSocket } from '@/hooks/useRealtimeSync';
import {
  MESSENGER_OPEN_AGENT_CHAT_FROM_NOTIFICATION,
  MESSENGER_OPEN_THREAD_FROM_NOTIFICATION,
  type MessengerOpenAgentChatFromNotificationDetail,
  type MessengerOpenThreadFromNotificationDetail,
} from '@/constants/messengerEvents';
import {
  buildMessengerBatchNotificationContent,
  buildMessengerNotificationContent,
} from '@/lib/messengerNotificationPreview';
import { unwrapMessengerMessageSocketPayload, unwrapMessengerMessagesBatchSocketPayload } from '@/lib/messengerSocketPayload';
import {
  attachNotificationClickNavigate,
  agentChatNotificationTag,
  messengerThreadNotificationTag,
  shouldSuppressAgentChatNotification,
  shouldSuppressMessengerMessageNotification,
  showSystemNotification,
} from '@/notifications/notificationOrchestrator';

function dispatchOpenThread(detail: MessengerOpenThreadFromNotificationDetail): void {
  window.dispatchEvent(
    new CustomEvent(MESSENGER_OPEN_THREAD_FROM_NOTIFICATION, {
      detail,
    })
  );
}

function dispatchOpenAgentChat(detail: MessengerOpenAgentChatFromNotificationDetail): void {
  window.dispatchEvent(
    new CustomEvent(MESSENGER_OPEN_AGENT_CHAT_FROM_NOTIFICATION, {
      detail,
    })
  );
}

export function installMessengerNotificationChannel(currentUserPersonId: string | null): () => void {
  if (!currentUserPersonId) {
    return () => undefined;
  }
  const socket = getSocket();
  const onMsg = (raw: unknown) => {
    const { message: msg, thread } = unwrapMessengerMessageSocketPayload(raw);
    if (!msg?.thread_id || !msg.sender_id) return;

    const suppressed = shouldSuppressMessengerMessageNotification({
      threadId: msg.thread_id,
      senderPersonId: msg.sender_id,
      currentUserPersonId: currentUserPersonId,
    });
    if (suppressed) return;

    const { title, body } = buildMessengerNotificationContent(msg, thread);
    const tag = messengerThreadNotificationTag(msg.thread_id);
    const threadKind = thread?.thread_kind ?? 'direct';
    const isDirect = threadKind === 'direct';
    const peerId = isDirect ? thread?.peer_id ?? null : null;
    const peerName = isDirect ? thread?.peer_name ?? null : null;
    const chatTitle = thread?.title ?? null;

    const detail: MessengerOpenThreadFromNotificationDetail = {
      threadId: msg.thread_id,
      threadKind,
      peerId,
      peerName,
      title: chatTitle,
    };

    const notification = showSystemNotification({
      title,
      body,
      tag,
      data: { ...detail },
    });

    attachNotificationClickNavigate(notification, () => dispatchOpenThread(detail));
  };

  const onAgentMsg = (raw: unknown) => {
    if (!raw || typeof raw !== 'object') return;
    const chatIdRaw = (raw as { chat_id?: unknown }).chat_id;
    if (typeof chatIdRaw !== 'string' || !chatIdRaw.trim()) return;
    const chatId = chatIdRaw.trim();

    if (shouldSuppressAgentChatNotification({ chatId })) return;

    const previewRaw = (raw as { preview?: unknown }).preview;
    const preview = typeof previewRaw === 'string' ? previewRaw.trim() : '';
    const detail: MessengerOpenAgentChatFromNotificationDetail = { chatId };
    const notification = showSystemNotification({
      title: 'Астра',
      body: preview || 'Новый ответ в чате Астры',
      tag: agentChatNotificationTag(chatId),
      data: { ...detail },
    });
    attachNotificationClickNavigate(notification, () => dispatchOpenAgentChat(detail));
  };

  const onMessagesBatch = (raw: unknown) => {
    const parsed = unwrapMessengerMessagesBatchSocketPayload(raw);
    if (!parsed) return;
    const { messages, thread } = parsed;
    const first = messages[0]!;
    if (!first?.thread_id || !first.sender_id) return;

    const suppressed = shouldSuppressMessengerMessageNotification({
      threadId: first.thread_id,
      senderPersonId: first.sender_id,
      currentUserPersonId: currentUserPersonId,
    });
    if (suppressed) return;

    const { title, body } = buildMessengerBatchNotificationContent(messages, thread);
    const last = messages[messages.length - 1]!;
    const tag = messengerThreadNotificationTag(last.thread_id);
    const threadKind = thread?.thread_kind ?? 'direct';
    const isDirect = threadKind === 'direct';
    const peerId = isDirect ? thread?.peer_id ?? null : null;
    const peerName = isDirect ? thread?.peer_name ?? null : null;
    const chatTitle = thread?.title ?? null;

    const detail: MessengerOpenThreadFromNotificationDetail = {
      threadId: last.thread_id,
      threadKind,
      peerId,
      peerName,
      title: chatTitle,
    };

    const notification = showSystemNotification({
      title,
      body,
      tag,
      data: { ...detail },
    });

    attachNotificationClickNavigate(notification, () => dispatchOpenThread(detail));
  };

  socket.on('messenger:message', onMsg);
  socket.on('messenger:messages_batch', onMessagesBatch);
  socket.on('messenger:agent_chat_message', onAgentMsg);
  return () => {
    socket.off('messenger:message', onMsg);
    socket.off('messenger:messages_batch', onMessagesBatch);
    socket.off('messenger:agent_chat_message', onAgentMsg);
  };
}
